/**
 * Everything that touches the browser: which locale this device shows, where
 * that choice is kept, and the `<html lang>` that goes with it.
 *
 * Precedence, highest first:
 *
 *   1. `?lang=uk|en` on the URL. An organiser sharing a link can force one, and
 *      it STICKS: it is written to the device choice, because whoever sent the
 *      link meant it and the switcher is one tap away if they were wrong.
 *   2. The device's own saved choice.
 *   3. The tournament's default from `GET /api/settings`, remembered from the
 *      last visit so it costs no round trip on the next one.
 *   4. Ukrainian.
 *
 * `navigator.language` is deliberately NOT in that chain. The organiser's
 * setting is a decision about their event; a visiting phone set to English is
 * not, and the switcher is visible on every surface.
 */

import { isLocale, type Locale } from "../../shared/settings";
import { getLocale } from "./runtime";

const DEVICE_KEY = "score.locale";
/** Last seen `settings.locale.default`, so an English-default tournament does
 *  not flash Ukrainian on every single load while the settings fetch lands. */
const TOURNAMENT_KEY = "score.locale.tournament";

/** Safari in private mode throws on `localStorage`. A language preference is
 *  never worth a white screen. */
function read(key: string): string | null {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}

function write(key: string, value: string): void {
  try {
    localStorage.setItem(key, value);
  } catch {
    /* no persistence in this browser; the session still works */
  }
}

/** True once the human has expressed a preference, which is what stops a
 *  later settings fetch from overriding them. */
export function hasDeviceChoice(): boolean {
  return isLocale(read(DEVICE_KEY));
}

export function rememberDeviceChoice(locale: Locale): void {
  write(DEVICE_KEY, locale);
}

export function rememberTournamentDefault(locale: Locale): void {
  write(TOURNAMENT_KEY, locale);
}

/**
 * The locale to boot with, decided synchronously so nothing has to wait on a
 * fetch before painting.
 */
export function initialLocale(): Locale {
  const fromUrl = new URLSearchParams(window.location.search).get("lang");
  if (isLocale(fromUrl)) {
    rememberDeviceChoice(fromUrl);
    return fromUrl;
  }
  const device = read(DEVICE_KEY);
  if (isLocale(device)) return device;
  const tournament = read(TOURNAMENT_KEY);
  if (isLocale(tournament)) return tournament;
  return "uk";
}

/**
 * `<html lang>`. It decides how a screen reader pronounces the page and
 * whether the browser offers its own translation, so it has to follow the
 * switcher rather than being stamped once in `index.html`.
 */
export function applyDocumentLocale(locale: Locale = getLocale()): void {
  document.documentElement.lang = locale;
}
